import { useCallback, useEffect, useState } from 'react';
import type { HeaderVariant, OrgaVariant, RecapVariant, ResetVariant, VariantSpec } from './types';

export const ORGA_VARIANTS: VariantSpec<OrgaVariant>[] = [
  { id: 'metier', label: 'A — Par question métier', note: '8 sections, sous-groupes titrés. Identification remontée, Chaud et Froid repliés.' },
  { id: 'source', label: 'B — Par catégorie source', note: 'Sections de prod inchangées, nouveaux champs ajoutés dans les catégories du tableau source.' },
  { id: 'actuel', label: 'Production actuelle', note: 'Panneau de prod, sans les nouveaux filtres. Sert de point de comparaison.' },
];

export const RECAP_VARIANTS: VariantSpec<RecapVariant>[] = [
  { id: 'puces', label: 'Puces + compteurs', note: 'Récapitulatif des filtres actifs en haut du panneau, compteur par section.' },
  { id: 'compteurs', label: 'Compteurs seuls', note: 'Seul le compteur de chaque section signale un filtre actif.' },
];

export const RESET_VARIANTS: VariantSpec<ResetVariant>[] = [
  { id: 'lien', label: 'Lien « Tout effacer »', note: 'Lien discret à côté de « N filtres actifs » en haut ; pied : « appliqués directement sur la carte ».' },
  { id: 'secondaire', label: 'Bouton gris', note: '« Effacer les filtres (N) » gris avec icône, à gauche du pied, + rappel « appliqués directement ».' },
  { id: 'prod', label: 'Bouton bleu (prod)', note: 'Bouton bleu plein en bas à droite : perçu comme « Valider » par les utilisateurs.' },
];

export const HEADER_VARIANTS: VariantSpec<HeaderVariant>[] = [
  { id: 'defile', label: 'Défile avec les filtres', note: 'Recherche et filtres actifs défilent avec le reste du panneau.' },
  { id: 'fixe', label: 'Haut fixe', note: 'Recherche, filtres actifs et réinitialisation restent visibles en haut pendant le défilement.' },
];

/** Choice kept in the URL (`?orga=source&recap=compteurs&reinit=lien`) so a tester can share the exact screen. */
function useUrlChoice<T extends string>(key: string, specs: VariantSpec<T>[]) {
  const read = useCallback((): T => {
    const value = new URLSearchParams(window.location.search).get(key);
    return specs.find((s) => s.id === value)?.id ?? specs[0].id;
  }, [key, specs]);

  const [value, setValue] = useState<T>(read);

  useEffect(() => {
    const onPop = () => setValue(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [read]);

  const change = useCallback((next: T) => {
    setValue(next);
    const url = new URL(window.location.href);
    url.searchParams.set(key, next);
    window.history.replaceState({}, '', url);
  }, [key]);

  return [value, change] as const;
}

export const useOrga = () => useUrlChoice('orga', ORGA_VARIANTS);
export const useRecap = () => useUrlChoice('recap', RECAP_VARIANTS);
export const useReset = () => useUrlChoice('reinit', RESET_VARIANTS);
export const useHeader = () => useUrlChoice('entete', HEADER_VARIANTS);
